import { useEffect, useRef, useState } from 'react'
import { sdk } from '@farcaster/miniapp-sdk'
import { useAccount, useConnect, useSignMessage } from 'wagmi'
import PixelSprite from './components/PixelSprite'
import Leaderboard from './components/Leaderboard'
import InsertCoin from './components/InsertCoin'
import { scoreMessage, submitScore } from './api'
import StarSwarm from './games/StarSwarm'
import VectorDrift from './games/VectorDrift'
import GirderPanic from './games/GirderPanic'
import MunchMaze from './games/MunchMaze'
import Asteroids from './games/Asteroids'

const PALETTE = {
  p: '#ff2e88', // magenta
  c: '#26f7fd', // cyan
  y: '#ffd23f', // coin yellow
  g: '#39ff14', // phosphor green
  o: '#ff8c1a', // barrel orange
  w: '#e8e8f6', // bone white
}

const GAMES = [
  {
    id: 'starswarm',
    title: 'StarSwarm',
    tag: 'Galaga-style swarm shooter',
    Component: StarSwarm,
    accent: '#ff2e88',
    sprite: [
      'p..pp..p',
      '.p.pp.p.',
      'pppppppp',
      'pp.cc.pp',
      'pppppppp',
      '.p....p.',
      'p......p',
      '.p....p.',
    ],
  },
  {
    id: 'vectordrift',
    title: 'Vector Drift',
    tag: 'Invader wave defense',
    Component: VectorDrift,
    accent: '#39ff14',
    sprite: [
      '..g..g..',
      '...gg...',
      '.gggggg.',
      'gg.gg.gg',
      'gggggggg',
      'g.g..g.g',
      'g......g',
      '.g....g.',
    ],
  },
  {
    id: 'girderpanic',
    title: 'Girder Panic',
    tag: 'Barrel-dodging climb',
    Component: GirderPanic,
    accent: '#ff8c1a',
    sprite: [
      '..oooo..',
      '.o.oo.o.',
      'oo.oo.oo',
      'o.oooo.o',
      'o.oooo.o',
      'oo.oo.oo',
      '.o.oo.o.',
      '..oooo..',
    ],
  },
  {
    id: 'munchmaze',
    title: 'Munch Maze',
    tag: 'Pellet-chomping maze chase',
    Component: MunchMaze,
    accent: '#ffd23f',
    sprite: [
      '..yyyy..',
      '.yyyyyy.',
      'yyyyyy..',
      'yyyy....',
      'yyyy....',
      'yyyyyy..',
      '.yyyyyy.',
      '..yyyy..',
    ],
  },
  {
    id: 'asteroids',
    title: 'Asteroids',
    tag: 'Vector rock breaker',
    Component: Asteroids,
    accent: '#26f7fd',
    sprite: [
      '..www...',
      '.w...w..',
      'w.....w.',
      'w......w',
      '.w.....w',
      'w.....w.',
      '.w...w..',
      '..www...',
    ],
  },
]

function short(addr) {
  return addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : ''
}

// Never let a hanging SDK promise leave the app stuck on a blank screen.
function withTimeout(promise, ms, fallback) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ])
}

export default function App() {
  const [isReady, setIsReady] = useState(false)
  const [inMiniApp, setInMiniApp] = useState(false)
  const [screen, setScreen] = useState('home') // 'home' | '<gameId>' | 'lb:<gameId>'
  const [gameOver, setGameOver] = useState(null) // { score, status, rank }
  const [paidGame, setPaidGame] = useState(null) // gameId whose coin has been inserted
  const initialized = useRef(false)

  const { address, isConnected } = useAccount()
  const { connect, connectors, isPending } = useConnect()
  const { signMessageAsync } = useSignMessage()
  const addressRef = useRef(address)
  addressRef.current = address
  const signRef = useRef(signMessageAsync)
  signRef.current = signMessageAsync

  // shareable leaderboard links: #lb/<gameId>
  useEffect(() => {
    const fromHash = () => {
      const m = window.location.hash.match(/^#lb\/(\w+)$/)
      if (m && GAMES.some((g) => g.id === m[1])) setScreen(`lb:${m[1]}`)
    }
    fromHash()
    window.addEventListener('hashchange', fromHash)
    return () => window.removeEventListener('hashchange', fromHash)
  }, [])

  const go = (next) => {
    setScreen(next)
    setGameOver(null)
    setPaidGame(null) // leaving a game spends the credit — next play needs a new coin
    window.location.hash = next.startsWith('lb:')
      ? `#lb/${next.slice(3)}`
      : ''
  }

  const gameHooks = (gameId) => ({
    onGameOver: async (score) => {
      const addr = addressRef.current
      if (!addr) {
        setGameOver({ score, status: 'no-wallet' })
        return
      }
      // wallet signs the score so the backend can verify it's genuine
      setGameOver({ score, status: 'signing' })
      const ts = Math.floor(Date.now() / 1000)
      let signature
      try {
        signature = await signRef.current({
          message: scoreMessage(gameId, addr, score, ts),
        })
      } catch {
        setGameOver({ score, status: 'rejected' })
        return
      }
      setGameOver({ score, status: 'saving' })
      try {
        const res = await submitScore(gameId, addr, score, ts, signature)
        setGameOver({
          score,
          status: res.newBest ? 'new-best' : 'saved',
          rank: res.rank,
          best: res.best,
        })
      } catch {
        setGameOver({ score, status: 'error' })
      }
    },
    onRestart: () => setGameOver(null),
  })

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const init = async () => {
      let isMiniApp = false
      try {
        isMiniApp = await withTimeout(sdk.isInMiniApp(), 3000, false)
      } catch {
        /* not in a Farcaster client */
      }
      setInMiniApp(isMiniApp)
      try {
        await withTimeout(sdk.actions.ready(), 5000)
      } catch {
        /* splash dismissal is best-effort */
      }
      // Always leave the loading screen, even if the SDK misbehaved —
      // a stuck promise here is what makes the app look blank/frozen.
      setIsReady(true)
    }
    init()
  }, [])

  // Auto-connect the Farcaster wallet once loaded inside a Farcaster client
  useEffect(() => {
    if (isReady && inMiniApp && !isConnected && connectors.length > 0) {
      connect({ connector: connectors[0] })
    }
  }, [isReady, inMiniApp, isConnected, connectors, connect])

  if (!isReady) {
    return (
      <main className="shell">
        <p className="boot">LOADING…</p>
      </main>
    )
  }

  const lbGame = screen.startsWith('lb:')
    ? GAMES.find((g) => g.id === screen.slice(3))
    : null
  const active = GAMES.find((g) => g.id === screen)

  return (
    <main className="shell">
      <div className="scanlines" aria-hidden="true" />

      {lbGame ? (
        <>
          <header className="game-header">
            <button type="button" className="back-btn" onClick={() => go('home')}>
              ◀ HOME
            </button>
            <button
              type="button"
              className="back-btn"
              onClick={() => go(lbGame.id)}
            >
              ▶ PLAY
            </button>
            <h2 style={{ color: lbGame.accent }}>{lbGame.title.toUpperCase()}</h2>
          </header>
          <Leaderboard game={lbGame} accent={lbGame.accent} playerAddress={address} />
        </>
      ) : active ? (
        <>
          <header className="game-header">
            <button type="button" className="back-btn" onClick={() => go('home')}>
              ◀ BACK
            </button>
            <h2 style={{ color: active.accent }}>{active.title.toUpperCase()}</h2>
          </header>
          {paidGame === active.id ? (
            <active.Component hooks={gameHooks(active.id)} />
          ) : (
            <InsertCoin
              game={active}
              inMiniApp={inMiniApp}
              onSuccess={() => setPaidGame(active.id)}
            />
          )}
          {gameOver && (
            <div className="go-panel" style={{ '--accent': active.accent }}>
              <p className="go-score">FINAL SCORE: {gameOver.score}</p>
              <p className="go-status">
                {gameOver.status === 'no-wallet' &&
                  'OPEN IN FARCASTER TO SAVE SCORES'}
                {gameOver.status === 'signing' && 'SIGN IN YOUR WALLET TO SAVE…'}
                {gameOver.status === 'rejected' &&
                  'SIGNATURE DECLINED — SCORE NOT SAVED'}
                {gameOver.status === 'saving' && 'SAVING SCORE…'}
                {gameOver.status === 'new-best' &&
                  `NEW PERSONAL BEST · RANK #${gameOver.rank}`}
                {gameOver.status === 'saved' &&
                  `YOUR BEST: ${gameOver.best} · RANK #${gameOver.rank}`}
                {gameOver.status === 'error' && 'SCOREBOARD OFFLINE'}
              </p>
              <button
                type="button"
                className="go-lb-link"
                onClick={() => go(`lb:${active.id}`)}
              >
                ★ VIEW LEADERBOARD
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <header className="marquee">
            <h1>
              <span className="w1">OSE</span> <span className="w2">GAMES</span>
            </h1>
            <p className="insert-coin">
              {isConnected
                ? `PLAYER 1 · ${short(address)}`
                : isPending
                  ? 'CONNECTING WALLET…'
                  : inMiniApp
                    ? 'INSERT COIN'
                    : 'INSERT COIN — OPEN IN FARCASTER TO CONNECT'}
            </p>
          </header>

          <section className="cabinet-row">
            {GAMES.map((g) => (
              <button
                key={g.id}
                type="button"
                className="cabinet"
                style={{ '--accent': g.accent }}
                onClick={() => go(g.id)}
              >
                <div className="cabinet-screen">
                  <PixelSprite matrix={g.sprite} palette={PALETTE} scale={6} />
                </div>
                <div className="cabinet-label">
                  <span className="cabinet-title">{g.title.toUpperCase()}</span>
                  <span className="cabinet-tag">{g.tag}</span>
                </div>
                <span className="cabinet-play">▶ PLAY</span>
              </button>
            ))}
          </section>

          <footer className="footer">
            <p>5 CABINETS · MORE COMING SOON</p>
          </footer>
        </>
      )}
    </main>
  )
}
